/**
 * When a consultant's conversation was had, against the data it read (the 2026-10-24 sweep, item 4.3). The page opens on
 * the stored conversation, which can be weeks old: an answer about a mechanism the brief has since tested reads as
 * true now. So an old question says the day it was asked, and where the plant brief was brought up to date after
 * answers in the thread, a note says so at that point. Days are on this computer's clock, in the briefs' own form
 * ("12 Mar 2031"). Pure.
 */
import type { CopilotMessage } from "@/lib/api/copilot-imports";
import { formatDay } from "@/lib/plant-days";

/** An instant's day on this computer's clock ("2031-03-12"); null where it does not read. */
function dayOf(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** "Asked on 12 Mar 2031" for a question asked before today; null for one asked today, or a time that does not read. */
export function askedOnText(createdAt: string, now: Date = new Date()): string | null {
  const day = dayOf(createdAt);
  const today = dayOf(now.toISOString());
  if (day === null || today === null || day >= today) return null;
  return `Asked on ${formatDay(day)}`;
}

/**
 * Where the brief-changed note goes: after the last message written before the plant brief was brought up to date
 * (briefAt, its run's end), so every answer above it read the older brief. -1 where no message is older, or briefAt does
 * not read.
 */
export function briefChangedAfter(messages: readonly Pick<CopilotMessage, "created_at">[], briefAt: string | undefined): number {
  const at = briefAt ? Date.parse(briefAt) : Number.NaN;
  if (Number.isNaN(at)) return -1;
  let last = -1;
  messages.forEach((m, i) => {
    const t = Date.parse(m.created_at);
    if (!Number.isNaN(t) && t < at) last = i;
  });
  return last;
}

/** The note: "The plant brief was brought up to date on 5 Mar 2031, after the answers above. ..." */
export function briefChangedText(briefAt: string): string {
  const day = dayOf(briefAt);
  const on = day ? ` on ${formatDay(day)}` : "";
  return `The plant brief was brought up to date${on}, after the answers above. The data may have changed since they were written.`;
}
